/**
 * Landing.jsx — the platform home (route /).
 *
 * 0.36.53 — Replaces the previous hard-coded marketing landing
 * (feature cards + green CTA button) with a data-driven render that
 * pulls content from the SYSTEM LANDING page (slug='landing',
 * page_type='landing'). Admins customize the home entirely via the
 * "Pages and forms" editor — titles, paragraphs, layers, columns,
 * images, form fields, the lot.
 *
 * No back link, no auto-h1: this is the platform home, not a sub-
 * page. Whatever the structure produces IS the home; nothing extra
 * is layered on top. (The page-metadata title — translation.title —
 * sets only the browser tab via document.title, not visible content.)
 *
 * If the landing page doesn't exist yet (fresh install before the
 * seed runs, or admin deleted it somehow despite the API refusing —
 * defense in depth), shows a small recovery hint pointing to setup.
 */

import { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import PageContentRenderer from '../components/PageContentRenderer';
import { getLandingPage } from '../api/pages';
import { buttonTemplatesPublicApi } from '../api/admin';
import styles from './Landing.module.css';
// 0.39.83 - Resolve =lib() page titles for the browser tab.
import { useResolvedTitle, resolveTitleText } from '../lib/resolveTitleText';
// 0.45.59 - Back to top on the home page too (it was on general pages only).
import HenaketScrollTop from '../components/HenaketScrollTop';
// 0.40.08 - Brand text for the browser tab suffix.
import { useSystemIdentity, tabTitle } from '../lib/systemIdentity';
// 0.49.29 - A home page that needs signing in shows the login page here.
import { isLoginRequired } from '../lib/requireLogin';
import { useAuth } from '../context/AuthContext';
import LoginPage from './LoginPage';


export default function Landing() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [needsLogin, setNeedsLogin] = useState(false);
  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // 0.36.76 — Button templates for resolving template:N variants.
  const [buttonTemplates, setButtonTemplates] = useState(() => buttonTemplatesPublicApi.cached() || []);

  // 0.39.83 - Browser tab title, resolving a =lib() landing title and
  // re-resolving when the library cache hydrates.
  const resolvedTabTitle = useResolvedTitle(page ? page.title : '', language);
  const systemIdentity = useSystemIdentity();
  useEffect(() => {
    if (!page) return;
    // 0.40.08 - Brand suffix from config, not the hardcoded name.
    // 0.44.75 / 0.44.78 - "Page title - System name" (Settings > System
    // name and icon); with no system name, the page title alone.
    document.title = tabTitle(resolvedTabTitle);
  }, [page, resolvedTabTitle, systemIdentity]);

  useEffect(() => {
    let cancelled = false;
    buttonTemplatesPublicApi.list()
      .then((data) => { if (!cancelled) setButtonTemplates(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setButtonTemplates([]); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // 0.36.59 — Fetch the landing by role (page_type='landing') via
    // the new /api/pages/landing-page endpoint, NOT by slug. This is
    // what makes "Set as home page" work: admins can promote any
    // page to be the home and this fetch follows the role wherever
    // it lives, no slug bookkeeping needed.
    getLandingPage(language)
      .then(data => {
        if (cancelled) return;
        setNeedsLogin(false);
        setPage(data);
        // 0.39.83 - Title set reactively below (resolves =lib()).
      })
      .catch(err => {
        if (cancelled) return;
        // 0.49.29 - The home page needs signing in (its "Visitors not
        // signed in see" setting): the login page, at this address.
        if (isLoginRequired(err)) { setNeedsLogin(true); setPage(null); return; }
        setError(err);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // Again on signing in or out.
  }, [language, user ? user.id : null]); // eslint-disable-line react-hooks/exhaustive-deps

  if (needsLogin && !user) return <LoginPage afterLogin="/" />;

  if (loading) {
    return (
      <main className={styles.landingMain}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              {/* 0.49.29 - Nothing visible while the home page loads (it
                  used to say "Loading…" in English on every language). */}
              <div aria-busy="true" style={{ minHeight: 'var(--space-5)' }} />
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error || !page) {
    // Defense-in-depth: the landing should always exist (system seed
    // creates it, API refuses delete). If it's somehow missing,
    // show a recovery hint instead of a blank page.
    return (
      <main className={styles.landingMain}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h1 style={{ marginTop: 'var(--space-5)' }}>
                Home page unavailable
              </h1>
              <p>
                The home page could not be loaded. An administrator can set
                one in Pages and forms, or load the built-in pages on the
                server with <code>python -m app.seed</code>.
              </p>
            </div>
          </div>
        </div>
      </main>
    );
  }

  // Happy path: render the structure inside <main>. No back link, no
  // auto-h1 — the structure carries everything visible on the home.
  // 0.45.43 - The flush_top pull-up (0.45.05) is gone: the header has
  // no bottom margin, so a first Layer starts under the sub-header.
  // 0.45.59 - Back to top, from the page's settings.scroll_top, as on
  // general pages (StaticPage). The home page had the setting in the
  // editor but never drew the button.
  const st = page.settings && page.settings.scroll_top;
  const scrollTopLabel = (() => {
    const l = st && st.label;
    if (typeof l === 'string') return l.trim();
    if (l && typeof l === 'object') {
      if (l[language]) return l[language];
      const first = Object.values(l).find(v => typeof v === 'string' && v.trim());
      return (first || '').trim();
    }
    return '';
  })();
  return (
    <main className={styles.landingMain}>
      <PageContentRenderer structure={page.structure} buttonTemplates={buttonTemplates}
                           rules={page.rules || []} settings={page.settings || null}
                           pageSlug={page.slug || ''} />
      {st && st.enabled && (
        <HenaketScrollTop
          label={resolveTitleText(scrollTopLabel, language)}
          showFromPercent={Number.isFinite(st.percent) ? st.percent : 100}
          visibleSeconds={Number.isFinite(st.seconds) ? st.seconds : 3}
          onlyWhenScrollingUp={st.up_only !== false}
        />
      )}
    </main>
  );
}
